import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Eye, FileText } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api/v1`;

interface Submission {
  id: string;
  title: string;
  description: string;
  status: string;
  submitted_by_user_name: string;
  asset: any;
  assets: any[];
  caption: string | null;
  created_at: string;
  agency_reviews: any[];
}

const AgencyReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/submissions`, {
        params: { status: 'AGENCY_REVIEW', page_size: 50 }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedSubmission) return;
    
    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${selectedSubmission.id}/agency/accept`, {
        comments: comments || undefined
      });
      setShowAcceptModal(false);
      setSelectedSubmission(null);
      setComments('');
      await fetchPendingReviews();
      alert('Creative accepted successfully!');
    } catch (error: any) {
      console.error('Failed to accept:', error);
      alert(error.response?.data?.detail || 'Failed to accept creative');
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestEdits = async () => {
    if (!selectedSubmission) return;
    
    if (!comments.trim()) {
      alert('Please provide comments explaining what edits are needed');
      return;
    }
    
    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${selectedSubmission.id}/agency/request-edits`, {
        comments: comments
      });
      setShowEditRequestModal(false);
      setSelectedSubmission(null);
      setComments('');
      await fetchPendingReviews();
      alert('Edit request sent successfully!');
    } catch (error: any) {
      console.error('Failed to request edits:', error);
      alert(error.response?.data?.detail || 'Failed to request edits');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Creative Review Queue
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Review submissions from Marketing Executives
          </p>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              No pending reviews
            </h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              All submissions have been reviewed.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Desktop Table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Submitted By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Assets
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {submission.title}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                          {submission.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {submission.submitted_by_user_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(submission.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {submission.assets?.length || 0} file(s)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => navigate(`/submissions/${submission.id}`)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setShowAcceptModal(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 min-h-[44px]"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setShowEditRequestModal(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white rounded-md hover:bg-orange-700 min-h-[44px]"
                        >
                          <AlertCircle className="w-4 h-4 mr-1" />
                          Request Edits
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {submissions.map((submission) => (
                <div key={submission.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    {submission.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                    {submission.description}
                  </p>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-4">
                    <div>By: {submission.submitted_by_user_name}</div>
                    <div>Date: {new Date(submission.created_at).toLocaleDateString()}</div>
                    <div>Assets: {submission.assets?.length || 0} file(s)</div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => navigate(`/submissions/${submission.id}`)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setShowAcceptModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 min-h-[44px]"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setShowEditRequestModal(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 min-h-[44px]"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Request Edits
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Accept Modal */}
      {showAcceptModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Accept Creative
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Accept "{selectedSubmission.title}" and move it forward in the workflow?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comments (optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                rows={3}
                placeholder="Add any comments or feedback..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAcceptModal(false);
                  setSelectedSubmission(null);
                  setComments('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 min-h-[44px]"
                disabled={processing}
              >
                {processing ? 'Accepting...' : 'Accept'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Edits Modal */}
      {showEditRequestModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Request Edits
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Request changes to "{selectedSubmission.title}"
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comments <span className="text-red-500">*</span>
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white"
                rows={4}
                placeholder="Explain what edits are needed..."
                required
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Please provide clear instructions on what needs to be changed
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditRequestModal(false);
                  setSelectedSubmission(null);
                  setComments('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleRequestEdits}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 min-h-[44px]"
                disabled={processing}
              >
                {processing ? 'Sending...' : 'Request Edits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyReviewPage;
